import { type EntityDescriptor } from '../entities';
import {
  type ControlledFlag,
  type ControlledSegment,
  type TargetingClause,
  type TargetingRule,
} from '../types';

import { IN_OP, SEGMENT_MATCH_OP, variationIsOn } from './analyzeTargeting';

export type ServedReason =
  | 'OFF'
  | 'TARGET_MATCH'
  | 'RULE_MATCH'
  | 'FALLTHROUGH';

export interface ServedValue {
  isOn: boolean;
  reason: ServedReason;
}

/** `undefined` when the targeting alone can't tell. */
type Match = boolean | undefined;

interface ResolveContext {
  descriptor: EntityDescriptor;
  entityId: string;
  segmentsByKey: Map<string, ControlledSegment>;
}

const negated = (match: Match, negate: boolean): Match =>
  match === undefined || !negate ? match : !match;

/** True if any is true, false if all are false. */
const anyOf = (matches: Match[]): Match => {
  if (matches.includes(true)) return true;
  return matches.every((match) => match === false) ? false : undefined;
};

/** False if any is false, true if all are true. */
const allOf = (matches: Match[]): Match => {
  if (matches.includes(false)) return false;
  return matches.every((match) => match === true) ? true : undefined;
};

const matchAttributeClause = (
  clause: TargetingClause,
  { descriptor, entityId }: ResolveContext
): Match => {
  // The entity is the only attribute known here; any other one could match.
  if (clause.attribute !== descriptor.clauseAttribute || clause.op !== IN_OP) {
    return undefined;
  }
  const listed = clause.values.some((value) => String(value) === entityId);
  return negated(listed, clause.negate);
};

const isInSegment = (segmentKey: string, context: ResolveContext): Match => {
  const segment = context.segmentsByKey.get(segmentKey);
  if (!segment || segment.unbounded) return undefined;

  if (context.descriptor.isContextKey) {
    if (segment.included.includes(context.entityId)) return true;
    if (segment.excluded.includes(context.entityId)) return false;
  }

  return anyOf(
    segment.rules.map((rule) =>
      allOf(
        rule.clauses.map((clause) =>
          clause.op === SEGMENT_MATCH_OP
            ? undefined
            : matchAttributeClause(clause, context)
        )
      )
    )
  );
};

const matchClause = (
  clause: TargetingClause,
  context: ResolveContext
): Match => {
  if (clause.op !== SEGMENT_MATCH_OP) {
    return matchAttributeClause(clause, context);
  }
  const inAny = anyOf(
    clause.values.map((value) => isInSegment(String(value), context))
  );
  return negated(inAny, clause.negate);
};

const matchRule = (rule: TargetingRule, context: ResolveContext): Match =>
  allOf(rule.clauses.map((clause) => matchClause(clause, context)));

const findTarget = (
  flag: ControlledFlag,
  { descriptor, entityId }: ResolveContext
) =>
  descriptor.isContextKey
    ? flag.targets.find(
        (target) =>
          (target.contextKind ?? 'user') === descriptor.contextKind &&
          target.values.includes(entityId)
      )
    : undefined;

const resolveRules = (
  flag: ControlledFlag,
  context: ResolveContext
): ServedValue | undefined | 'noMatch' => {
  // The first matching rule wins, so an undecided one hides the rest.
  const decisive = flag.rules
    .filter((rule) => !rule.disabled)
    .find((rule) => matchRule(rule, context) !== false);

  if (!decisive) return 'noMatch';
  if (matchRule(decisive, context) === undefined || decisive.isRollout) {
    return undefined;
  }
  return {
    isOn: variationIsOn(flag.variations, decisive.variation),
    reason: 'RULE_MATCH',
  };
};

export interface ResolveServedValueParams {
  flag: ControlledFlag;
  descriptor: EntityDescriptor;
  entityId: string;
  segments: ControlledSegment[];
}

/**
 * What the entity is served, worked out from the targeting just read from the
 * flag provider, in the provider's order: off, individual targets, rules, then
 * the default. The services evaluate against a copy that can trail a change by
 * a few minutes, so right after a write this is the up-to-date answer.
 *
 * Returns `undefined` when the targeting can't decide on its own: rollouts,
 * prerequisites, or a condition on anything but the entity's own id.
 */
export const resolveServedValue = ({
  flag,
  descriptor,
  entityId,
  segments,
}: ResolveServedValueParams): ServedValue | undefined => {
  if (!flag.enabled) {
    return {
      isOn: variationIsOn(flag.variations, flag.offVariation),
      reason: 'OFF',
    };
  }
  if (flag.prerequisiteKeys.length > 0) return undefined;

  const context: ResolveContext = {
    descriptor,
    entityId,
    segmentsByKey: new Map(segments.map((segment) => [segment.key, segment])),
  };

  const target = findTarget(flag, context);
  if (target) {
    return {
      isOn: variationIsOn(flag.variations, target.variation),
      reason: 'TARGET_MATCH',
    };
  }

  const fromRules = resolveRules(flag, context);
  if (fromRules !== 'noMatch') return fromRules;

  if (flag.fallthrough.isRollout) return undefined;
  return {
    isOn: variationIsOn(flag.variations, flag.fallthrough.variation),
    reason: 'FALLTHROUGH',
  };
};
